import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Palette, DollarSign, MessageCircle, HelpCircle, ArrowRight } from "lucide-react";

const ServicesSection = () => {
  const services = [
    {
      icon: Palette,
      title: "Artwork Consignments",
      description: "Have a piece by one of Florida's legendary Highwaymen artists? Submit your artwork for consignment and connect with serious collectors.",
      link: "/consign",
      buttonText: "Submit Consignment",
      gradient: "from-emerald-500/10 via-teal-500/10 to-blue-500/10",
      iconColor: "text-emerald-400",
      iconBg: "bg-emerald-500/20",
      borderColor: "border-emerald-400/30"
    },
    {
      icon: DollarSign,
      title: "Art Appraisals",
      description: "Get professional appraisals for your Florida Highwaymen artwork from our team of experts with decades of experience.",
      link: "/appraisal",
      buttonText: "Request Appraisal",
      gradient: "from-blue-500/10 via-purple-500/10 to-pink-500/10",
      iconColor: "text-blue-400",
      iconBg: "bg-blue-500/20",
      borderColor: "border-blue-400/30"
    },
    {
      icon: MessageCircle,
      title: "Contact Us",
      description: "Have questions about our auctions, services, or the Florida Highwaymen? Our team is here to help.",
      link: "/contact",
      buttonText: "Get in Touch",
      gradient: "from-purple-500/10 via-pink-500/10 to-rose-500/10",
      iconColor: "text-purple-400",
      iconBg: "bg-purple-500/20",
      borderColor: "border-purple-400/30"
    },
    {
      icon: HelpCircle,
      title: "Platform FAQs",
      description: "New to online auctions? Find answers to common questions about bidding, payments, shipping, and more.",
      link: "/faq",
      buttonText: "View FAQs",
      gradient: "from-teal-500/10 via-cyan-500/10 to-sky-500/10",
      iconColor: "text-teal-400",
      iconBg: "bg-teal-500/20",
      borderColor: "border-teal-400/30"
    }
  ];

  return (
    <section className="relative py-16 md:py-24 overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Sophisticated Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-teal-500/3 rounded-full blur-3xl" />
      </div>

      {/* Subtle grid overlay */}
      <div 
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='32' height='32' viewBox='0 0 32 32' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M0 0h1v1H0zM16 16h1v1h-1z'/%3E%3C/g%3E%3C/svg%3E")`,
          backgroundSize: '32px 32px'
        }}
      />

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-block rounded-full px-6 py-3 mb-6 bg-white/5 backdrop-blur-xl text-emerald-300/90 border border-white/10 shadow-xl">
            <span className="font-light text-sm lg:text-base tracking-wider uppercase">Our Services</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-white mb-6 tracking-tight">
            How Can We Help You?
          </h2>
          <p className="text-lg md:text-xl text-white/70 max-w-3xl mx-auto font-light leading-relaxed">
            From consignments to appraisals, we offer comprehensive services for collectors, sellers, and enthusiasts of Florida Highwaymen art
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="group relative overflow-hidden bg-white/5 backdrop-blur-xl border border-white/10 hover:border-white/20 transition-all duration-500 h-full hover:transform hover:scale-[1.02] hover:shadow-2xl">
                  {/* Gradient overlay */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                  
                  <CardHeader className="relative">
                    <div className={`w-16 h-16 rounded-2xl ${service.iconBg} flex items-center justify-center border ${service.borderColor} backdrop-blur-xl mb-4 group-hover:scale-110 transition-transform duration-500`}>
                      <Icon className={`h-8 w-8 ${service.iconColor}`} />
                    </div>
                    <CardTitle className="text-xl font-light text-white tracking-wide">
                      {service.title}
                    </CardTitle>
                  </CardHeader>
                  
                  <CardContent className="relative space-y-6">
                    <CardDescription className="text-white/70 font-light leading-relaxed min-h-[80px]">
                      {service.description}
                    </CardDescription>
                    
                    <Link to={service.link} className="block">
                      <Button 
                        variant="ghost" 
                        className="w-full text-white/90 hover:text-white hover:bg-white/10 border border-white/20 group-hover:border-white/30 transition-all rounded-xl font-light"
                      >
                        {service.buttonText}
                        <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;

